/**
 * xr_governor — hold the headset's frame rate with the cheapest lever first (XR P1.8, 2026-09-28). Pure.
 *
 * Meta's WebXR performance guidance: measure against the budget; when the app cannot hold the rate, lower what the
 * GPU draws (foveation first — it is a per-session uniform with no rebuild), then the display rate (a missed 90 Hz
 * frame is a judder, a held 72 Hz frame is not), and only then the content (a VR level change rebuilds the post
 * chain and re-scatters foliage — a visible hitch, so it is rationed). Headroom walks the same ladder back, never
 * past where the headset started. The framebuffer scale is NOT a lever here: three applies it only before
 * setSession, so it stays fixed for the session.
 *
 * `governorStep` is called ~2× a second with the rolling snapshot (lib/xr_perf.ts) and returns at most one action.
 */

import { clampLevel, type VrLevel } from './xr_profile.js';

export interface GovernorState {
  /** Seconds the window has been over / under budget without a break. */
  overS: number;
  underS: number;
  /** Seconds since the last action of each kind (Infinity at session start). */
  sinceStepS: number;
  sinceLevelS: number;
}

export interface GovernorInputs {
  /** The rolling statistics. */
  frames: number;
  p95Ms: number;
  missShare: number;
  budgetMs: number;
  /** What is in force now. */
  foveation: number;
  hz: number;
  level: VrLevel;
  /** Where the headset started (the governor never goes "better" than this). */
  startFoveation: number;
  startHz: number;
  startLevel: VrLevel;
  /** What the session can do. */
  canFoveate: boolean;
  /**
   * May the governor change the VR LEVEL itself? A level change is a rebuild storm (the quality-changed fan-out:
   * foliage re-scatter, the warm cache emptied, ground / water / foliage shaders recompiled, settlements rebuilt) —
   * on the Quest 3 (2026-09-28) the governor's level step at 18 s landed with the stall that starved the database
   * socket and the reload that ended the session. Off: the player chooses a level (the Perf page, `?xrlevel`).
   */
  allowLevel?: boolean;
  rates: readonly number[];
}

export type GovernorAction =
  | { kind: 'foveation'; value: number; why: string }
  | { kind: 'hz'; value: number; why: string }
  | { kind: 'level'; value: VrLevel; why: string };

export const GOV = Object.freeze({
  /** Over budget: the 95th-percentile frame past 1.15 budgets, or a tenth of frames missed. */
  OVER_P95: 1.15,
  OVER_MISS: 0.1,
  /** Headroom: the 95th percentile under 0.7 budgets and almost no misses. */
  UNDER_P95: 0.7,
  UNDER_MISS: 0.01,
  OVER_HOLD_S: 2,
  UNDER_HOLD_S: 10,
  /** Between any two actions (the window restarts after each). */
  STEP_COOLDOWN_S: 3,
  /** Between two VR level changes (each is a rebuild). */
  LEVEL_COOLDOWN_S: 45,
  /** Foveation steps. */
  FOV_STEP: 0.33,
  /** Never ask for fewer than this many hertz (below 72 the compositor's own reprojection is the better answer). */
  MIN_HZ: 72,
  /** A snapshot needs this many frames to judge. */
  MIN_FRAMES: 60,
  /** No judgement in the first seconds of a session or after a settings change: those frames are shader compiles and
   *  asset uploads (the XR variant of every material), not the scene's cost. */
  WARMUP_S: 20,
});

export function governorInit(): GovernorState {
  return { overS: 0, underS: 0, sinceStepS: Infinity, sinceLevelS: Infinity };
}

const round2 = (v: number): number => Math.round(v * 100) / 100;

/**
 * Advance the governor by `dt` seconds and return the action to take now, if any. Mutates `s` (the caller keeps
 * one state per session and calls `resetXrPerf()` after an action, so the next judgement sees only new frames).
 */
export function governorStep(s: GovernorState, dt: number, g: GovernorInputs): GovernorAction | null {
  const step = Number.isFinite(dt) && dt > 0 ? Math.min(dt, 2) : 0;
  s.sinceStepS += step;
  s.sinceLevelS += step;
  if (!(g.frames >= GOV.MIN_FRAMES) || !(g.budgetMs > 0)) { s.overS = 0; s.underS = 0; return null; }

  const over = g.p95Ms > g.budgetMs * GOV.OVER_P95 || g.missShare > GOV.OVER_MISS;
  const under = g.p95Ms < g.budgetMs * GOV.UNDER_P95 && g.missShare < GOV.UNDER_MISS;
  s.overS = over ? s.overS + step : 0;
  s.underS = under ? s.underS + step : 0;
  if (s.sinceStepS < GOV.STEP_COOLDOWN_S) return null;

  const done = (a: GovernorAction): GovernorAction => {
    s.sinceStepS = 0; s.overS = 0; s.underS = 0;
    if (a.kind === 'level') s.sinceLevelS = 0;
    return a;
  };
  const rates = [...g.rates].map((r) => Math.round(r)).filter((r) => r > 0).sort((a, b) => a - b);

  if (s.overS >= GOV.OVER_HOLD_S) {
    const why = `p95 ${g.p95Ms.toFixed(1)} ms vs ${g.budgetMs.toFixed(1)} ms budget, ${(g.missShare * 100).toFixed(0)} % missed`;
    if (g.canFoveate && g.foveation < 1 - 1e-6) return done({ kind: 'foveation', value: round2(Math.min(1, g.foveation + GOV.FOV_STEP)), why });
    const lower = rates.filter((r) => r < Math.round(g.hz) && r >= GOV.MIN_HZ).pop();
    if (lower !== undefined) return done({ kind: 'hz', value: lower, why });
    if (g.allowLevel !== false && g.level < 2 && s.sinceLevelS >= GOV.LEVEL_COOLDOWN_S) return done({ kind: 'level', value: clampLevel(g.level + 1), why });
    return null;
  }

  if (s.underS >= GOV.UNDER_HOLD_S) {
    const why = `headroom: p95 ${g.p95Ms.toFixed(1)} ms vs ${g.budgetMs.toFixed(1)} ms budget`;
    // The reverse order: content first (the most visible loss), then the rate, then foveation.
    if (g.allowLevel !== false && g.level > g.startLevel && s.sinceLevelS >= GOV.LEVEL_COOLDOWN_S) return done({ kind: 'level', value: clampLevel(g.level - 1), why });
    const higher = rates.find((r) => r > Math.round(g.hz) && r <= Math.round(g.startHz));
    if (higher !== undefined) return done({ kind: 'hz', value: higher, why });
    if (g.canFoveate && g.foveation > g.startFoveation + 1e-6) return done({ kind: 'foveation', value: round2(Math.max(g.startFoveation, g.foveation - GOV.FOV_STEP)), why });
  }
  return null;
}
